/**
 * Palantir PMS - Software para la gestion de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creacion / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso esta restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/500.css";
import "@fontsource/montserrat/600.css";
import "@fontsource/montserrat/700.css";
import "@fontsource/montserrat/800.css";
import pilcoreLogo from "../assets/Logo_Pilcore_Vertical_Blanco.svg";
import "../LoginScreen.css";
import axios from "../api/axiosInstance";
import { completeLogin } from "../utils/completeLogin";


const resolveLoginErrorMessage = (error) => {
  if (error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "")) {
    return "The server is taking too long to respond. Please try again.";
  }

  if (!error?.response) {
    return "Unable to connect to the server. Check that the service is running and try again.";
  }

  if (error.response.status === 401) {
    return "Incorrect email or password.";
  }

  return error.response?.data?.error || "Unable to sign in. Please try again.";
};

/* ========= Small reset password modal ========= */
const ResetPasswordModal = ({
  show,
  initialEmail,
  onClose,
  onSubmit,
  loading,
}) => {
  const [localEmail, setLocalEmail] = useState(initialEmail || "");

  useEffect(() => {
    setLocalEmail(initialEmail || "");
  }, [initialEmail]);

  if (!show) return null;

  return (
    <div
      className="auth-modalBackdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="resetPasswordTitle"
    >
      <div className="auth-modalCard">
        <h6 id="resetPasswordTitle" className="auth-modalTitle">
          Reset Password
        </h6>

        <p className="auth-modalText">
          Please enter your email and we will send you a link to reset your password.
        </p>

        <div className="auth-group">
          <label className="auth-label">Email</label>
          <input
            type="email"
            className="auth-input"
            value={localEmail}
            onChange={(e) => setLocalEmail(e.target.value)}
            autoComplete="username"
            placeholder="you@example.com"
          />
        </div>

        <div className="auth-modalActions">
          <button
            type="button"
            className="auth-btn-secondary"
            onClick={() => {
              console.log("Reset Password modal: Cancel / close");
              onClose();
            }}
            disabled={loading}
          >
            Cancel
          </button>

          <button
            type="button"
            className="auth-btn-primary"
            onClick={() => {
              console.log("Sending reset password request for:", localEmail);
              onSubmit(localEmail);
            }}
            disabled={loading || !localEmail}
          >
            {loading ? "Sending..." : "Accept"}
          </button>
        </div>
      </div>
    </div>
  );
};
/* =============================================================== */

const LoginScreen = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const loginInFlightRef = useRef(false);
  const loginAbortRef = useRef(null);

  useEffect(() => () => loginAbortRef.current?.abort(), []);

  useEffect(() => {
    const hidePassword = () => setPasswordVisible(false);
    const releasePointer = (event) => {
      if (event.pointerType !== "mouse") hidePassword();
    };
    window.addEventListener("blur", hidePassword);
    window.addEventListener("pointerup", releasePointer);
    window.addEventListener("pointercancel", hidePassword);
    document.addEventListener("visibilitychange", hidePassword);
    return () => {
      window.removeEventListener("blur", hidePassword);
      window.removeEventListener("pointerup", releasePointer);
      window.removeEventListener("pointercancel", hidePassword);
      document.removeEventListener("visibilitychange", hidePassword);
    };
  }, []);

  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setPasswordVisible(false);
    if (loginInFlightRef.current) return;

    loginInFlightRef.current = true;
    const controller = new AbortController();
    loginAbortRef.current = controller;
    setLoginLoading(true);
    setError(null);

    try {
      const res = await axios.post(
        "/api/auth/login",
        { email, password },
        { timeout: 15_000, signal: controller.signal }
      );

      const data = res.data;

      if (controller.signal.aborted) return;
      if (!data?.token || !data?.user?.id) throw new Error("Invalid login response");
      await completeLogin(data, navigate, controller.signal);
      if (!controller.signal.aborted) setPassword("");
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error("Login error:", err);
      setError(resolveLoginErrorMessage(err));
    } finally {
      loginInFlightRef.current = false;
      if (!controller.signal.aborted) setLoginLoading(false);
    }
  };

  const openResetModal = () => {
    setPasswordVisible(false);
    console.log("Opening Reset Password modal");
    console.log("Prefill resetEmail with:", email);
    setResetEmail(email || "");
    setShowResetModal(true);
  };

  const closeResetModal = () => {
    console.log("Closing Reset Password modal");
    setShowResetModal(false);
    setResetLoading(false);
  };

  const submitResetPassword = async (emailToSend) => {
    setError(null);
    setResetLoading(true);
    try {
      await axios.post("/api/users/reset-password-request", { email: emailToSend });
      console.log("Reset password request sent");
      alert(
        "If this email exists, a password reset link has been sent. Please, check your Junkbox if you do not find it in your Inbox. For first access, ask your administrator for an invitation."
      );
      closeResetModal();
    } catch (err) {
      console.error("Error sending reset password:", err);
      setError("Could not send reset email. Try again later.");
      setResetLoading(false);
    }
  };

  return (
    <div className="auth-page auth-loginPage">
      <img src={pilcoreLogo} alt="Pilcore watermark" className="auth-watermark" />

      <div className="auth-shell">
        
        <form onSubmit={handleLogin} className="auth-card" aria-busy={loginLoading}>
          <div className="auth-group">
            <label className="auth-label" htmlFor="login-email">Email</label>
            <input
              id="login-email"
              type="email"
              className="auth-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loginLoading}
              autoComplete="username"
            />
          </div>

          <div className="auth-group">
            <label className="auth-label" htmlFor="login-password">Password</label>
            <div className="auth-passwordField">
              <input
                id="login-password"
                type={passwordVisible ? "text" : "password"}
                className="auth-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loginLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="auth-passwordReveal"
                aria-label="Show password temporarily"
                aria-controls="login-password"
                aria-pressed={passwordVisible}
                title="Hover or press and hold to show password"
                disabled={loginLoading}
                onPointerEnter={(e) => {
                  if (!loginLoading && e.pointerType === "mouse") setPasswordVisible(true);
                }}
                onPointerDown={(e) => {
                  if (loginLoading || e.button !== 0) return;
                  e.preventDefault();
                  setPasswordVisible(true);
                }}
                onPointerLeave={() => setPasswordVisible(false)}
                onBlur={() => setPasswordVisible(false)}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    setPasswordVisible(true);
                  }
                  if (e.key === "Escape") setPasswordVisible(false);
                }}
                onKeyUp={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    setPasswordVisible(false);
                  }
                }}
              >
                {passwordVisible ? <FiEyeOff aria-hidden="true" /> : <FiEye aria-hidden="true" />}
              </button>
            </div>
          </div>

          <div className="auth-actions">
            <button
              type="button"
              className="auth-link"
              onClick={openResetModal}
              disabled={loginLoading}
            >
              Forgot password?
            </button>
          </div>

          {error && <div className="auth-error" role="alert">{error}</div>}

          <button type="submit" className="auth-btn-primary" disabled={loginLoading}>
            {loginLoading ? (
              <span className="auth-btn-content">
                <span className="auth-btn-spinner" aria-hidden="true" />
                Signing in...
              </span>
            ) : (
              "Login"
            )}
          </button>

        </form>
      </div>

      <ResetPasswordModal
        show={showResetModal}
        initialEmail={resetEmail}
        onClose={closeResetModal}
        onSubmit={submitResetPassword}
        loading={resetLoading}
      />
    </div>
  );
};

export default LoginScreen;
